import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Col,
  Input,
  InputGroup,
  InputGroupAddon,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  ListGroup,
  ListGroupItem,
} from "reactstrap";
import ServerServices from "./ServerServices";
import DisplayWhen from "./common/base/DisplayWhen";
import "./ServersServices.scss";
import userHasRole from "./shared/utils/authUtils";
import {
  technicianRole,
  adminEditorRole,
  adminViewerRole,
  csmRole,
  csmMaintenanceRole,
  techopsRole,
} from "./shared/constants";
import { createCloudInstance, getCloudIdentifiers } from "../services/machineService";
import { getServerStatus } from "../services/locationService";
import { restartWithToast } from "../services/machineOperationsService";
import { getMachineByName } from "../services/inferenceMachineService";

function ServerServicesList({ servers, store }) {
  const history = useHistory();
  const [podname, setPodname] = useState("");
  const [resolvingServer, setResolvingServer] = useState(null);
  const [restartingServer, setRestartingServer] = useState(null);
  const [serverStatusModal, setServerStatusModal] = useState(false);
  const [serverStatusData, setServerStatusData] = useState(null);
  const [serverStatusLoading, setServerStatusLoading] = useState(false);
  const [currentServerName, setCurrentServerName] = useState("");
  const [cloudIdentifierModal, setCloudIdentifierModal] = useState(false);
  const [cloudIdentifiers, setCloudIdentifiers] = useState([]);
  const [selectedCloudIdentifier, setSelectedCloudIdentifier] = useState("");
  const [loadingCloudIdentifiers, setLoadingCloudIdentifiers] = useState(false);

  const openCloudIdentifierModal = () => {
    setLoadingCloudIdentifiers(true);
    setCloudIdentifierModal(true);
    setSelectedCloudIdentifier("");

    getCloudIdentifiers()
      .then((identifiers) => {
        setCloudIdentifiers(identifiers);
        setLoadingCloudIdentifiers(false);
      })
      .catch((error) => {
        console.error("Error fetching cloud identifiers:", error);
        toast.error("Failed to load cloud identifiers");
        setLoadingCloudIdentifiers(false);
        setCloudIdentifiers([]);
      });
  };

  const createPod = (cloudIdentifier = null) => {
    createCloudInstance(cloudIdentifier)
      .then((response) => {
        toast.success(`Sucessfully created instance: ${response.data.hostname}`);
        setPodname(response.data.hostname);
        setCloudIdentifierModal(false);
      })
      .catch((error) => {
        if (error.response && error.response.status < 500) {
          toast.error(error.response.data.detail || "An error occurred while creating server");
        }
        console.error("Error generating cloud server:", error);
      });
  };

  const handleCreateWithCloudIdentifier = () => {
    if (selectedCloudIdentifier) {
      createPod(selectedCloudIdentifier);
    } else {
      toast.error("Please select a cloud identifier before creating the server");
    }
  };

  const restartCloudInference = async (server) => {
    setRestartingServer(server);
    try {
      await restartWithToast([server], "simone-inference");
    } finally {
      setRestartingServer(null);
    }
  };

  const goToInferenceMachine = async (serverName) => {
    if (resolvingServer) return;
    setResolvingServer(serverName);
    try {
      // The server key is the machine hostname; resolve it to the InferenceMachine
      // primary key expected by the /inferencemachines/:id detail route.
      const { data: machine } = await getMachineByName(serverName);
      history.push(`/inferencemachines/${machine.id}`);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        toast.error(`No inference machine found for ${serverName}`);
      } else {
        toast.error("Failed to open inference machine");
      }
      console.error("Error resolving inference machine:", error);
    } finally {
      setResolvingServer(null);
    }
  };

  const getServerStatusData = (serverName) => {
    setCurrentServerName(serverName);
    setServerStatusLoading(true);
    setServerStatusModal(true);
    setServerStatusData(null);

    getServerStatus(serverName)
      .then((response) => {
        setServerStatusData(response.data);
        setServerStatusLoading(false);
      })
      .catch((error) => {
        console.error("Error getting server status:", error);
        setServerStatusData([{ name: "Error", details: "Failed to fetch server status" }]);
        setServerStatusLoading(false);
        toast.error("Failed to get server status");
      });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Copied to clipboard!");
    });
  };

  const renderStatusData = (data) => {
    if (!data) return null;

    // If it's a simple object with "name": "ok"
    if (typeof data === "object" && data.name === "ok") {
      return (
        <ListGroupItem className="text-success">
          <i className="fas fa-check-circle mr-2" />
          Server status: OK
        </ListGroupItem>
      );
    }

    // If it's an array of errors
    if (Array.isArray(data)) {
      return data.map((item, index) => (
        <ListGroupItem key={index} className="text-danger">
          <div className="font-weight-bold">
            <i className="fas fa-exclamation-triangle mr-2" />
            {item.name}
          </div>
          {item.details && (
            <div className="mt-2">
              {Array.isArray(item.details) ? (
                <ul className="mb-0" style={{ color: "#ff8c00" }}>
                  {item.details.map((detail, detailIndex) => (
                    <li key={detailIndex} style={{ color: "#ff8c00" }}>
                      {detail}
                    </li>
                  ))}
                </ul>
              ) : (
                <span style={{ color: "#ff8c00" }}>{item.details}</span>
              )}
            </div>
          )}
        </ListGroupItem>
      ));
    }

    // If it's a simple object with name and details
    if (typeof data === "object" && data.name && data.name !== "ok") {
      return (
        <ListGroupItem className="text-danger">
          <div className="font-weight-bold">
            <i className="fas fa-exclamation-triangle mr-2" />
            {data.name}
          </div>
          {data.details && (
            <div className="mt-2">
              {Array.isArray(data.details) ? (
                <ul className="mb-0" style={{ color: "#ff8c00" }}>
                  {data.details.map((detail, detailIndex) => (
                    <li key={detailIndex} style={{ color: "#ff8c00" }}>
                      {detail}
                    </li>
                  ))}
                </ul>
              ) : (
                <span style={{ color: "#ff8c00" }}>{data.details}</span>
              )}
            </div>
          )}
        </ListGroupItem>
      );
    }

    // Fallback for other formats
    return (
      <ListGroupItem>
        <pre>{JSON.stringify(data, null, 2)}</pre>
      </ListGroupItem>
    );
  };

  return (
    <>
      <Card>
        <CardHeader className="mb-2">
          <h5 className="card-category">Servers</h5>
        </CardHeader>

        {Object.keys(servers.status).map((server, index) => (
          <CardBody key={`server-${index}`}>
            <h4>
              {server}
              <Button className="ml-3" disabled={Boolean(resolvingServer)} onClick={() => goToInferenceMachine(server)}>
                {resolvingServer === server ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2" /> Opening...
                  </>
                ) : (
                  <>
                    <i className="fas fa-microchip mr-2" /> Inference Machine
                  </>
                )}
              </Button>
              <DisplayWhen
                condition={servers.status[server].id && (!userHasRole(csmRole) || userHasRole(csmMaintenanceRole))}
              >
                <a
                  className="ml-3"
                  href={`/remotecontrol?server=${servers.status[server].id}&command=screenshot${
                    store?.id != null ? `&store=${store.id}` : ""
                  }`}
                >
                  <Button>Remote Control</Button>
                </a>
                <a
                  className="ml-3"
                  href={`https://grafana.prod.veesion.io/d/tew9c45/store-server-details?orgId=1&from=now-24h&to=now&var-server=${server}&dtab=-at-a-glance`}
                  target="_blank" // popup
                  rel="noopener noreferrer"
                >
                  <Button>Server Overview</Button>
                </a>
              </DisplayWhen>
              <DisplayWhen
                condition={
                  userHasRole(csmRole) ||
                  userHasRole(csmMaintenanceRole) ||
                  userHasRole(adminEditorRole) ||
                  userHasRole(adminViewerRole)
                }
              >
                <Button className="ml-3 btn-info" onClick={() => getServerStatusData(server)}>
                  <i className="fas fa-info-circle mr-2" /> Server Status
                </Button>
              </DisplayWhen>
              <DisplayWhen condition={!store.local_inference}>
                <Button
                  className="ml-3 btn-primary"
                  disabled={Boolean(restartingServer)}
                  onClick={() => restartCloudInference(server)}
                >
                  <i className="fas fa-power-off mr-2" /> Restart
                </Button>
              </DisplayWhen>
            </h4>
            {servers.status[server].is_cloud && servers.status[server].cloud_identifier && (
              <div className="cloud-identifier-badge">
                <i className="fas fa-cloud mr-1" />
                {servers.status[server].cloud_identifier}
              </div>
            )}
            <DisplayWhen condition={store.local_inference}>
              <ServerServices server={servers.status[server]} serverName={server} />
            </DisplayWhen>
          </CardBody>
        ))}

        <DisplayWhen
          condition={
            !store.local_inference &&
            (userHasRole(technicianRole) || userHasRole(adminEditorRole) || userHasRole(techopsRole))
          }
        >
          <Col md={12}>
            <Button className="float-left" onClick={() => openCloudIdentifierModal()}>
              Create Cloud Server
            </Button>
            <InputGroup>
              <Input type="text" value={podname} />
              <InputGroupAddon addonType="append">
                <Button color="secondary" onClick={() => copyToClipboard(podname)}>
                  <i className="fas fa-copy" />
                </Button>
              </InputGroupAddon>
            </InputGroup>
          </Col>
        </DisplayWhen>
      </Card>
      {/* Modal pour afficher le statut du serveur */}
      <Modal isOpen={serverStatusModal} toggle={() => setServerStatusModal(false)} size="lg">
        <ModalHeader toggle={() => setServerStatusModal(false)}>Server Status: {currentServerName}</ModalHeader>
        <ModalBody>
          {serverStatusLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <p className="mt-2">Loading server status...</p>
            </div>
          ) : (
            <ListGroup>{renderStatusData(serverStatusData)}</ListGroup>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setServerStatusModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal pour sélectionner le cloud identifier */}
      <Modal
        isOpen={cloudIdentifierModal}
        toggle={() => setCloudIdentifierModal(false)}
        contentClassName="image-modal bg-transparent cloud-identifier-modal"
      >
        <ModalBody style={{ width: "500px" }}>
          <div style={{ padding: "20px" }}>
            <h4 className="modal-title">Select Cloud Identifier</h4>
            {loadingCloudIdentifiers ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
                <p className="mt-2 modal-loading-text">Loading cloud identifiers...</p>
              </div>
            ) : (
              <div>
                <p className="modal-text">Choose a cloud identifier for the new server:</p>
                {cloudIdentifiers.length > 0 ? (
                  <Input
                    type="select"
                    value={selectedCloudIdentifier}
                    onChange={(e) => setSelectedCloudIdentifier(e.target.value)}
                    className="modal-select"
                  >
                    <option value="">Select a cloud identifier...</option>
                    {cloudIdentifiers.map((identifier, index) => (
                      <option key={index} value={JSON.stringify(identifier)}>
                        {identifier[0]} - {identifier[1]}
                      </option>
                    ))}
                  </Input>
                ) : (
                  <p className="modal-error-text">
                    No cloud identifiers available. The server will be created with default settings.
                  </p>
                )}
              </div>
            )}
            <div className="modal-buttons">
              <Button color="secondary" onClick={() => setCloudIdentifierModal(false)} className="btn-cancel">
                Cancel
              </Button>
              <Button
                color="primary"
                onClick={handleCreateWithCloudIdentifier}
                className="btn-create"
                disabled={!selectedCloudIdentifier}
              >
                Create Server
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}

export default ServerServicesList;
